/**
 * "Set your password" from the talent approval email (Nautica, 2 Oct 2026, Ric Okoniewski), at /reset-password?setup=…
 * (src/pages/ResetPassword.jsx routes it here). The link carries a
 * server-signed token, good for 30 days and until the first sign-in (src/server/services/setPasswordToken.js). The
 * talent picks a password and is signed straight in.
 */
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { LockIcon } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '@contexts/AuthContext'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import BrandLogo from '@components/BrandLogo'

const MIN = 8

export default function SetPassword() {
  const [params] = useSearchParams()
  const token = params.get('setup') || ''
  const { signIn } = useAuth()
  const [state, setState] = useState({ loading: true, email: null, name: null, error: null })
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/talent/set-password?token=${encodeURIComponent(token)}`)
      .then(r => r.json().then(b => ({ ok: r.ok, b })))
      .then(({ ok, b }) => { if (!cancelled) setState(ok ? { loading: false, email: b.email, name: b.name, error: null } : { loading: false, error: b.message || 'This link is not valid.' }) })
      .catch(() => { if (!cancelled) setState({ loading: false, error: 'Something went wrong. Please try again.' }) })
    return () => { cancelled = true }
  }, [token])

  const submit = async (e) => {
    e.preventDefault()
    if (password.length < MIN) { toast.error(`Use at least ${MIN} characters.`); return }
    if (password !== confirm) { toast.error('The passwords do not match.'); return }
    setSaving(true)
    try {
      const res = await fetch('/api/talent/set-password', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, password }) })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.message || 'Could not set your password.')
      toast.success('Password set. Signing you in…')
      const result = await signIn(body.email, password)
      if (!result?.success) setSaving(false)
    } catch (err) {
      toast.error(err.message)
      setSaving(false)
    }
  }

  const first = String(state.name || '').trim().split(/\s+/)[0]
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="flex justify-center mb-6"><Link to="/"><BrandLogo size="lg" variant="icon" showText={false} /></Link></div>
        <h1 className="text-3xl font-display font-bold text-foreground text-center">{first ? `Welcome, ${first}!` : 'Set your password'}</h1>
        {state.loading && <p className="mt-6 text-center text-muted-foreground">Checking your link…</p>}
        {state.error && (
          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p>{state.error}</p>
            <div className="mt-3 flex gap-4">
              <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link>
              <Link to="/forgot-password" className="font-semibold text-primary hover:underline">Forgot password</Link>
            </div>
          </div>
        )}
        {state.email && (
          <form onSubmit={submit} className="mt-6 bg-card rounded-xl shadow-sm border border-border p-6 space-y-4">
            <p className="text-sm text-muted-foreground">Choose a password for <b className="text-foreground">{state.email}</b>. You&apos;ll use it to sign in to your dashboard.</p>
            <Input label="Password" type="password" icon={<LockIcon className="w-5 h-5 text-muted-foreground" />} value={password} onChange={e => setPassword(e.target.value)} placeholder={`At least ${MIN} characters`} autoComplete="new-password" />
            <Input label="Confirm password" type="password" icon={<LockIcon className="w-5 h-5 text-muted-foreground" />} value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="new-password" />
            <Button type="submit" className="w-full" disabled={saving}>{saving ? 'Saving…' : 'Set password and sign in'}</Button>
          </form>
        )}
      </div>
    </div>
  )
}
